import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispute } from '../../context/DisputeContext';
import { sendInvitation } from '../../utils/api';
import ProgressBar from '../../components/ProgressBar';

const TONE_OPTIONS = [
  {
    id: 'collaborative',
    title: 'Collaborative',
    description: 'Warm and approachable',
    icon: '🤝',
    preview: "We'd like to work together to find a fair solution to our dispute in a friendly, collaborative way...",
  },
  {
    id: 'formal',
    title: 'Formal',
    description: 'Professional and business-like',
    icon: '💼',
    preview: 'We would like to invite you to participate in a formal alternative dispute resolution process...',
  },
  {
    id: 'urgent',
    title: 'Urgent',
    description: 'Direct and time-sensitive',
    icon: '⚡',
    preview: 'This matter requires prompt attention. We invite you to participate in an expedited dispute resolution process...',
  },
];

const InvitePage = () => {
  const navigate = useNavigate();
  const { dispute, updateDispute } = useDispute();

  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [selectedTone, setSelectedTone] = useState('collaborative');
  const [customMessage, setCustomMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!dispute || !dispute.id || !dispute.type) {
      navigate('/start');
    }
  }, [dispute, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!recipientName.trim() || !recipientEmail.trim()) {
      setError('Please provide the other party\'s name and email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail)) {
      setError('Please provide a valid email address');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const data = await sendInvitation({
        disputeId: dispute.id,
        recipientName,
        recipientEmail,
        tone: selectedTone,
        customMessage: customMessage.trim() || null,
      });

      updateDispute(data);
      navigate('/status');
    } catch (err) {
      setError(err.message || 'Failed to send invitation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!dispute) {
    return null;
  }

  const selectedToneOption = TONE_OPTIONS.find(t => t.id === selectedTone);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <ProgressBar current={4} total={5} phase="Send Invitation" />

      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold mb-3">Invite the Other Party</h1>
        <p className="text-gray-600">
          We'll send them an invitation to participate in this dispute resolution process.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Recipient Information */}
        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-4">Other Party's Information</h2>

          <div className="mb-4">
            <label htmlFor="recipientName" className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            <input
              id="recipientName"
              type="text"
              className="input"
              placeholder="John Doe"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              required
              disabled={submitting}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="recipientEmail" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              id="recipientEmail"
              type="email"
              className="input"
              placeholder="john@example.com"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              required
              disabled={submitting}
            />
            <p className="text-xs text-gray-500 mt-2">
              They will receive an email invitation to respond to this dispute
            </p>
          </div>
        </div>

        {/* Tone Selection */}
        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-4">Choose Your Communication Tone</h2>
          <p className="text-sm text-gray-600 mb-4">
            Select how you'd like to approach the other party. This affects the wording of the invitation email.
          </p>

          <div className="grid md:grid-cols-3 gap-4">
            {TONE_OPTIONS.map((tone) => (
              <label
                key={tone.id}
                className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                  selectedTone === tone.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-primary-300'
                }`}
              >
                <input
                  type="radio"
                  name="tone"
                  value={tone.id}
                  checked={selectedTone === tone.id}
                  onChange={(e) => setSelectedTone(e.target.value)}
                  className="sr-only"
                  disabled={submitting}
                />
                <div className="text-center">
                  <div className="text-4xl mb-2">{tone.icon}</div>
                  <div className="font-bold mb-1">{tone.title}</div>
                  <div className="text-xs text-gray-600">{tone.description}</div>
                </div>
                {selectedTone === tone.id && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </label>
            ))}
          </div>

          {/* Preview */}
          {selectedToneOption && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-xs font-medium text-gray-500 mb-2">Email Preview:</div>
              <p className="text-sm text-gray-700 italic">{selectedToneOption.preview}</p>
            </div>
          )}
        </div>

        {/* Optional Custom Message */}
        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-4">
            Add a Personal Message <span className="text-sm font-normal text-gray-500">(Optional)</span>
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Include a personal note that will be added to the invitation email. Keep it brief and respectful.
          </p>

          <textarea
            className="input min-h-[100px]"
            placeholder="Example: I believe we can resolve this matter fairly and quickly through this process. I look forward to working with you to find a solution."
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            maxLength={500}
            disabled={submitting}
          />
          <div className="text-xs text-gray-500 mt-2 text-right">
            {customMessage.length}/500 characters
          </div>
        </div>

        {/* What Happens Next */}
        <div className="card bg-blue-50 border-blue-200 mb-6">
          <h3 className="font-bold mb-3">What happens next?</h3>
          <ol className="text-sm text-gray-700 space-y-2">
            <li className="flex items-start">
              <span className="font-bold mr-2 text-primary-500">1.</span>
              <span>We'll send {recipientName || 'the other party'} an email invitation to respond</span>
            </li>
            <li className="flex items-start">
              <span className="font-bold mr-2 text-primary-500">2.</span>
              <span>They'll have 48 hours to review and provide their perspective</span>
            </li>
            <li className="flex items-start">
              <span className="font-bold mr-2 text-primary-500">3.</span>
              <span>Our AI will analyze both sides and suggest fair settlement options</span>
            </li>
            <li className="flex items-start">
              <span className="font-bold mr-2 text-primary-500">4.</span>
              <span>An attorney will review and help finalize any agreement</span>
            </li>
          </ol>

          <div className="mt-4 pt-4 border-t border-blue-300">
            <p className="text-xs text-gray-600">
              <strong>Reminder:</strong> Your settlement parameters and assessment answers remain private
              and will never be shared with the other party. Only the dispute type and your claim will be visible.
            </p>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            type="button"
            onClick={() => navigate('/parameters')}
            className="btn-secondary"
            disabled={submitting}
          >
            Back
          </button>

          <button
            type="submit"
            className="btn-primary"
            disabled={submitting}
          >
            {submitting ? (
              <span className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sending Invitation...
              </span>
            ) : (
              'Send Invitation'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InvitePage;
